import { assertCapability, type ActorSource, type Capability } from '@lemontaigu/platform';

/**
 * C3's L2 capability gate (ADR-0013 §4, finding F3, increment 3).
 *
 * WHY THIS THROWS WHERE C4 RETURNS A `Result`. ADR-0013 §4 says the gate returns
 * a Result because a denial is an expected outcome, not an exception — and in C4
 * that is free, since every `pms-core` port already returns `Result<T>`. C3 does
 * not: its stores throw, its services return `Promise<void>` / `Promise<string>`,
 * and its views already do `catch (e) { setErr(e.message) }`. Converting C3 to
 * Result would be a repo-wide signature refactor with nothing to do with
 * authorization, so the gate adapts to the idiom of the context it guards. The
 * ADR's intent — the denial is a domain outcome carrying a staff-facing French
 * message, not a leaked PostgREST error — is what matters, and is preserved.
 *
 * WHAT IT IS NOT. Not the boundary. The browser holds a real Postgres session,
 * so RLS and the `SECURITY DEFINER` RPCs decide (ADR-0013 §7). Every capability
 * named at L2 has a matching policy at L4 — E11 enforces that direction, E12 the
 * other (no gate stricter than its policy).
 *
 * ⚠ DO NOT ADD THIS TO AN IDENTITY-SCOPED USE CASE. See ladder §3.19: where RLS
 * grants access by identity (`employee_id = staff_employee_id()`) as well as by
 * capability, a capability gate is STRICTER than the database and locks out the
 * people it was meant to serve. That is why `BoardPresenceService` — the
 * employee self-tap path — deliberately has no gate.
 */
export class CapabilityDeniedError extends Error {
  constructor(
    message: string,
    /** For the log, never for the banner. */
    readonly denied: Capability,
  ) {
    super(message);
    this.name = 'CapabilityDeniedError';
  }
}

/** Throws `CapabilityDeniedError` when the actor lacks `cap`. */
export function requireCapability(actor: ActorSource, cap: Capability): void {
  const check = assertCapability(actor.current(), cap);
  if (!check.ok) throw new CapabilityDeniedError(check.message, check.denied);
}
